import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { getImageUrl } from '../../services/api'
import { useSportContext } from '../../contexts/SportContext'
import { useLeague } from '../../contexts/LeagueContext'
import { contrast, DEFAULT_PRIMARY, ON_ACCENT_DARK, ON_ACCENT_LIGHT, parseHex } from '../../shared/color/accent'
import { streamAsk } from './stream'
import type { ChatTurn, FanAiConfig } from './types'
import { useFanAiConfig } from './useFanAiConfig'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import type widgetsEn from '@/i18n/catalogs/site-core/widgets/en'

/** The Fan AI words in the active language (site-core widgets.fanAi). */
type FanAiWords = typeof widgetsEn.fanAi
const useFanAiWords = (): FanAiWords => useT(siteCoreMessages).widgets.fanAi

// #613: markdown is its own chunk; the bubble shows plain text for the moment it loads.
const AnswerMarkdown = lazy(() => import('./AnswerMarkdown'))
import { setFanAiLauncherVisible } from './launcherStore'

// The fan's active sport, or null outside a SportProvider. useContext runs
// on every call either way, so the hook order is stable.
function useActiveSportId(): string | null {
  try {
    return useSportContext().activeSportId || null
  } catch {
    return null
  }
}

// Text colour for anything filled with the league's primary (launcher, Send,
// the fan's own messages): white or dark ink, whichever reads better on it.
// Falls back to the default primary outside a LeagueProvider (same pattern
// as useActiveSportId).
function useThemePrimary(): string | null {
  try {
    return useLeague().league?.theme?.primary || null
  } catch {
    return null
  }
}

function useOnPrimary(): string {
  return onPrimaryFor(useThemePrimary())
}

function onPrimaryFor(themePrimary: string | null): string {
  const primary = parseHex(themePrimary) ?? DEFAULT_PRIMARY
  return contrast(ON_ACCENT_LIGHT, primary) >= contrast(ON_ACCENT_DARK, primary) ? ON_ACCENT_LIGHT : ON_ACCENT_DARK
}

// Number of recent turns we send back to the server with each new question.
// Keeps the prompt tight while preserving short-term conversational context.
const HISTORY_TURNS = 8

const STORAGE_PREFIX = 'fan-ai:'

function makeConversationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function makeId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export default function FanAiBubble() {
  const sportId = useActiveSportId()
  const { config } = useFanAiConfig(sportId)
  const [open, setOpen] = useState(false)
  // Tell the corner badge the launcher is on screen so it stacks above it (#489).
  const launcherShown = Boolean(config) && !open
  useEffect(() => {
    setFanAiLauncherVisible(launcherShown)
    return () => setFanAiLauncherVisible(false)
  }, [launcherShown])

  // Don't even render the bubble until config arrives — avoids a flicker on
  // tenants without the feature enabled. A null config also covers a league
  // that isn't entitled / is paused / is disabled (#328): no bubble at all.
  if (!config) return null

  return (
    <>
      <BubbleButton config={config} open={open} onOpen={() => setOpen(true)} />
      {open && (
        <ChatSheet
          config={config}
          sportId={sportId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

// ---- Bubble button ---------------------------------------------------------

function BubbleButton({
  config,
  open,
  onOpen,
}: {
  config: FanAiConfig
  open: boolean
  onOpen: () => void
}) {
  const onPrimary = useOnPrimary()
  const t = useFanAiWords()
  if (open) return null
  // Bottom offset: website templates with a bottom bar raise it through
  // --site-fab-offset (default 0, so classic is unchanged).
  const avatar = getImageUrl(config.assistant_avatar_url)
  return (
    <button
      onClick={onOpen}
      className="fixed bottom-[calc(1rem_+_var(--site-fab-offset,0px))] right-4 z-40 flex items-center gap-2 rounded-full bg-primary shadow-lg shadow-primary/30 hover:scale-105 active:scale-95 transition-transform pl-2 pr-4 py-2 sm:bottom-[calc(1.5rem_+_var(--site-fab-offset,0px))] sm:right-6"
      style={{ color: onPrimary }}
      aria-label={t.launcher(config.assistant_name)}
    >
      <div className="w-8 h-8 rounded-full bg-white/20 overflow-hidden flex items-center justify-center text-lg">
        {avatar ? (
          <img src={avatar} alt="" className="w-full h-full object-cover" />
        ) : (
          <span aria-hidden>💬</span>
        )}
      </div>
      <span className="text-sm font-semibold">{config.assistant_name}</span>
    </button>
  )
}

// ---- Chat sheet ------------------------------------------------------------

function ChatSheet({
  config,
  sportId,
  onClose,
}: {
  config: FanAiConfig
  sportId: string | null
  onClose: () => void
}) {
  const onPrimary = useOnPrimary()
  const words = useFanAiWords()
  const conversationIdRef = useRef<string>('')
  if (!conversationIdRef.current) conversationIdRef.current = makeConversationId()

  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorBanner, setErrorBanner] = useState<string | null>(null)
  // The server says the assistant is switched off for this league (plan,
  // subscription or league status changed while the sheet was open). Not an
  // error: show a calm notice and stop accepting questions.
  const [unavailable, setUnavailable] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Persist the open conversation across reloads so reopening the sheet
  // doesn't blow away a fan's in-flight Q&A.
  const storageKey = useMemo(() => `${STORAGE_PREFIX}${window.location.host}`, [])
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed.turns)) setTurns(parsed.turns)
        if (parsed.conversationId) conversationIdRef.current = parsed.conversationId
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        conversationId: conversationIdRef.current,
        turns,
      }))
    } catch { /* ignore */ }
  }, [turns, storageKey])

  // Auto-scroll to the end of the thread when new content streams in.
  useEffect(() => {
    if (!scrollerRef.current) return
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
  }, [turns])

  // Cancel any in-flight stream when the sheet unmounts.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  // Lock body scroll while the sheet is open. Tailwind's fixed inset
  // covers everything; without this the page behind would scroll under
  // the sheet on iOS Safari.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  async function send(question: string) {
    const q = question.trim()
    if (!q || busy || unavailable) return
    setErrorBanner(null)
    setInput('')

    const userTurn: ChatTurn = { id: makeId(), role: 'user', content: q }
    const assistantTurn: ChatTurn = {
      id: makeId(),
      role: 'assistant',
      content: '',
      streaming: true,
      tools: [],
    }
    setTurns((t) => [...t, userTurn, assistantTurn])
    setBusy(true)

    const ctrl = new AbortController()
    abortRef.current = ctrl

    // Send last N user/assistant turns as conversation history.
    const history = turns.slice(-HISTORY_TURNS).map((t) => ({
      role: t.role,
      content: t.content,
    }))

    try {
      const stream = streamAsk(
        { question: q, conversationId: conversationIdRef.current, history, sportId },
        ctrl.signal
      )
      for await (const ev of stream) {
        if (ev.type === 'token' && ev.text) {
          const text = ev.text
          setTurns((t) => t.map((turn) => {
            if (turn.id !== assistantTurn.id) return turn
            // Separator between turn-flushed chunks: if we already have
            // content and the new chunk doesn't start a new line, insert
            // one. Prevents "...try again:Having trouble..." mash-ups
            // when the model speaks both before and after a tool call.
            const needsBreak =
              turn.content.length > 0 && !/\n\s*$/.test(turn.content) && !/^\s/.test(text)
            return { ...turn, content: turn.content + (needsBreak ? '\n\n' : '') + text }
          }))
        } else if (ev.type === 'tool_call' && ev.tool) {
          const tool = ev.tool
          setTurns((t) => t.map((turn) =>
            turn.id === assistantTurn.id
              ? { ...turn, tools: [...(turn.tools ?? []), tool] }
              : turn
          ))
        } else if (ev.type === 'error' && ev.unavailable) {
          const msg = ev.message || words.unavailable
          setTurns((t) => t.map((turn) =>
            turn.id === assistantTurn.id
              ? { ...turn, streaming: false, declined: 'unavailable', content: msg }
              : turn
          ))
          setUnavailable(true)
        } else if (ev.type === 'error' && ev.message) {
          const msg = ev.message
          setTurns((t) => t.map((turn) =>
            turn.id === assistantTurn.id
              ? { ...turn, streaming: false, declined: 'error', content: turn.content || msg }
              : turn
          ))
          setErrorBanner(msg)
        } else if (ev.type === 'done') {
          setTurns((t) => t.map((turn) =>
            turn.id === assistantTurn.id
              ? { ...turn, streaming: false, declined: ev.declined ?? null }
              : turn
          ))
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // User closed the sheet or sent a new question — nothing to do.
        return
      }
      setTurns((t) => t.map((turn) =>
        turn.id === assistantTurn.id
          ? { ...turn, streaming: false, declined: 'network', content: turn.content || words.connectionLost }
          : turn
      ))
      setErrorBanner(words.connectionLostRetry)
    } finally {
      setBusy(false)
      abortRef.current = null
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void send(input)
  }

  const showWelcome = turns.length === 0

  return (
    <div className="fixed inset-0 z-50 flex flex-col sm:items-end sm:justify-end sm:p-6 bg-black/50 backdrop-blur-sm">
      {/* Backdrop tap to close (desktop) */}
      <button
        type="button"
        className="hidden sm:block absolute inset-0 cursor-default"
        aria-label={words.close}
        onClick={onClose}
      />

      <div className="relative w-full sm:w-[420px] sm:max-h-[85vh] flex-1 sm:flex-none flex flex-col bg-bg-secondary sm:rounded-2xl shadow-2xl overflow-hidden">
        <Header config={config} onClose={onClose} />

        <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {showWelcome && (
            <Welcome
              config={config}
              onPick={(q) => send(q)}
            />
          )}
          {turns.map((turn) => (
            <Message key={turn.id} turn={turn} />
          ))}
        </div>

        {unavailable ? (
          <div role="status" className="px-4 py-2 text-xs bg-white/5 text-white/70 border-t border-white/10">
            {words.unavailableNotice(config.assistant_name)}
          </div>
        ) : errorBanner && (
          <div className="px-4 py-2 text-xs bg-red-500/10 text-red-300 border-t border-red-500/20">
            {errorBanner}
          </div>
        )}

        <form onSubmit={handleSubmit} className="border-t border-white/10 p-3 flex items-end gap-2 bg-bg-primary">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (!busy) void send(input)
              }
            }}
            rows={1}
            placeholder={unavailable ? words.placeholderUnavailable : busy ? words.placeholderBusy : words.placeholder}
            className="flex-1 resize-none bg-white/5 text-white placeholder-white/40 rounded-lg px-3 py-2 text-base sm:text-sm outline-none focus:ring-2 focus:ring-primary/40 max-h-32"
            disabled={busy || unavailable}
            maxLength={500}
          />
          <button
            type="submit"
            disabled={busy || unavailable || !input.trim()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-hover transition"
            style={{ color: onPrimary }}
          >
            {words.send}
          </button>
        </form>
      </div>
    </div>
  )
}

function Header({ config, onClose }: { config: FanAiConfig; onClose: () => void }) {
  const t = useFanAiWords()
  const avatar = getImageUrl(config.assistant_avatar_url)
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-bg-primary">
      <div className="w-9 h-9 rounded-full bg-primary/20 overflow-hidden flex items-center justify-center text-lg">
        {avatar ? (
          <img src={avatar} alt="" className="w-full h-full object-cover" />
        ) : (
          <span aria-hidden>💬</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white text-sm truncate">{config.assistant_name}</div>
        <div className="text-xs text-white/50">{t.subtitle}</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={t.close}
        className="text-white/60 hover:text-white/90 p-2 -mr-2"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 5l10 10M15 5L5 15" />
        </svg>
      </button>
    </div>
  )
}

function Welcome({
  config,
  onPick,
}: {
  config: FanAiConfig
  onPick: (q: string) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white/85 leading-relaxed">
        {config.welcome_message}
      </div>
      {config.suggested_questions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {config.suggested_questions.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onPick(q)}
              className="text-xs bg-white/5 hover:bg-white/10 text-white/80 rounded-full px-3 py-1.5 border border-white/10 transition"
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Message({ turn }: { turn: ChatTurn }) {
  const onPrimary = useOnPrimary()
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-primary rounded-2xl rounded-br-md px-3.5 py-2 max-w-[85%] text-sm shadow-sm" style={{ color: onPrimary }}>
          {turn.content}
        </div>
      </div>
    )
  }
  // assistant — render as markdown so tables, lists, bold, etc. show correctly
  return (
    <div className="flex justify-start">
      <div className="bg-white/[0.06] text-white/95 rounded-2xl rounded-bl-md px-3.5 py-2.5 max-w-[92%] text-sm leading-relaxed shadow-sm border border-white/5">
        {turn.content ? (
          <div className="fan-ai-prose">
            <Suspense fallback={<p className="my-1.5 whitespace-pre-wrap">{turn.content}</p>}>
              <AnswerMarkdown content={turn.content} />
            </Suspense>
            {turn.streaming && <Caret />}
          </div>
        ) : (
          turn.streaming ? <ThinkingDots tools={turn.tools ?? []} /> : null
        )}
      </div>
    </div>
  )
}

function ThinkingDots({ tools }: { tools: string[] }) {
  // While the model is fetching data we show a friendly status. The tool
  // names we use are descriptive enough to surface; the user's seen "stat
  // leaders" / "standings" elsewhere on the site.
  const t = useFanAiWords()
  const last = tools[tools.length - 1]
  const label = last ? humaniseTool(last, t) : t.tool.thinking
  return (
    <span className="text-white/60 inline-flex items-center gap-1.5">
      <span className="inline-flex gap-0.5">
        <Dot delay={0} />
        <Dot delay={120} />
        <Dot delay={240} />
      </span>
      <span className="text-xs">{label}…</span>
    </span>
  )
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-white/40 inline-block animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    />
  )
}

function Caret() {
  return <span className="inline-block w-1 h-3.5 bg-white/40 ml-0.5 align-middle animate-pulse" />
}

function humaniseTool(name: string, t: FanAiWords): string {
  switch (name) {
    case 'get_standings': return t.tool.standings
    case 'get_next_games': return t.tool.nextGames
    case 'get_recent_results': return t.tool.recentResults
    case 'get_game_result': return t.tool.gameResult
    case 'get_stat_leaders': return t.tool.statLeaders
    case 'get_player_stats': return t.tool.playerStats
    case 'search_players': return t.tool.searchPlayers
    case 'get_team_info': return t.tool.teamInfo
    case 'get_head_to_head': return t.tool.headToHead
    case 'get_league_overview': return t.tool.leagueOverview
    default: return t.tool.other
  }
}
